import { useMemo } from 'react';
import styled from 'styled-components';
import { Options } from 'react-select';
import { ripeLemon } from '@paradigm/design-system/src/colors';
import { useSubmitted } from '@paradigm/features/src/forms/submitted-context';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import Collapsible, {
  CollapsibleContainer,
} from '#/components/UnifiedRfqs/components/Collapsible';
import {
  H1,
  ResponsiveH1,
} from '#/components/UnifiedRfqs/components/counterparty-selector/primitives';
import { OptionTypeBase } from '@paradigm/design-system/src/components/Select/types';

import SearchInput from './SearchInput';
import Filters, { FiltersContainer } from './Filters';
import CpsList from './CpsList';

interface Props {
  title?: string;
  error?: string;
  mode?: 'builder' | 'filter';
  value?: Options<OptionTypeBase>;
  onChange?: (values: Options<OptionTypeBase>) => void;
  showSearch?: boolean;
  showFilters?: boolean;
  showContextMenu?: boolean;
}

export default function CounterpartySelector({
  title = 'Select Counterparties',
  error,
  mode = 'builder',
  showSearch = true,
  showFilters = true,
}: Props) {
  const isMobile = useIsMobile();
  const { didSubmit } = useSubmitted();
  const hasError = didSubmit && Boolean(error);
  return useMemo(
    () =>
      isMobile ? (
        <MobileContent
          title={title}
          hasError={hasError}
          showSearch={showSearch}
          showFilters={showFilters}
          mode={mode}
        />
      ) : (
        <DesktopContent
          title={title}
          hasError={hasError}
          showSearch={showSearch}
          showFilters={showFilters}
          mode={mode}
        />
      ),
    [isMobile, hasError, title, showSearch, showFilters, mode],
  );
}

interface ContentProps {
  title: string;
  hasError: boolean;
  showSearch: boolean;
  showFilters: boolean;
  mode: 'builder' | 'filter';
}

function DesktopContent({
  title,
  hasError,
  showSearch,
  showFilters,
  mode,
}: ContentProps) {
  return (
    <Container hasError={hasError}>
      <H1>{title}</H1>
      {showSearch && <SearchInput />}
      {showFilters && (
        <Filters showLps showFavorites showClear={mode === 'builder'} />
      )}
      <CpsList mode={mode} />
    </Container>
  );
}

function MobileContent({
  title,
  hasError,
  showSearch,
  showFilters,
  mode,
}: ContentProps) {
  return (
    <Container hasError={hasError}>
      <ResponsiveH1>{title}</ResponsiveH1>
      {showFilters && <Filters showLps showFavorites />}
      <Collapsible title="Counterparties">
        {showSearch && <SearchInput />}
        {showFilters && <Filters showClear={mode === 'builder'} />}
        <CpsList mode={mode} />
      </Collapsible>
    </Container>
  );
}

const Container = styled.div.attrs({
  'data-testid': 'select-counterparties',
})<{ hasError: boolean }>`
  --h-pad: 1rem;
  --top-pad: 1rem;
  position: relative;
  display: flex;
  flex-direction: column;
  width: 305px;
  padding: var(--top-pad) var(--h-pad) 0;
  background: #08182a;
  border-radius: 0 7px 0 3px;
  height: 725px;
  ${(props) => props.hasError && `border: 1px solid ${ripeLemon};`}

  ${H1} {
    margin-bottom: 1rem;
  }

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    height: auto;
    width: 100%;
    background: transparent;
    padding-top: 0;

    ${CollapsibleContainer} {
      button {
        line-height: 2.375rem;
      }

      ${FiltersContainer} {
        display: block;
        text-align: right;
      }
    }
  }
`;
